/* ============================================================================
   ROWMAN — animations
   The atmospheric systems from the approved source, ported unchanged in
   behaviour but wrapped so they mount/unmount cleanly in the Theme Editor and
   respect the motion setting. Every rAF loop is reference-counted; nothing
   keeps running after its section is removed.

   Ported from rowman__53_.html:
     · 3D background parallax engine     (scroll cross-fade + mouse drift)
     · Philosophy scroll-driven presence  (--phs / --off, cross tilt, thread)
     · Hero letter dispersal
     · Patient ECG trace
     · Wellness radar (live hexagon breathing)
     · Gift card 3D flip + cursor tilt
   ============================================================================ */
(function () {
  'use strict';

  var R = window.ROWMAN;
  if (!R) return;

  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
  function smoothstep(t) { t = clamp(t, 0, 1); return t * t * (3 - 2 * t); }
  function easeInOutSine(t) { return -(Math.cos(Math.PI * t) - 1) / 2; }

  /* ═══════════════════ 3D BACKGROUND PARALLAX ENGINE ═══════════════════
     Scroll-linked cross-fade between three layers plus subtle mouse drift.
     Layer images are supplied through theme settings (image_picker). */

  R.register({
    name: 'BgParallax',
    scope: '[data-bg-stage]',
    mount: function (stage) {
      var layers = Array.prototype.slice.call(stage.querySelectorAll('.bg-layer'));
      if (layers.length < 3) return;

      var count = parseInt(stage.getAttribute('data-layer-count') || '0', 10);
      if (count === 0) {
        layers.forEach(function (l) { l.style.opacity = 0; });
        return;
      }

      var mouseX = 0.5, mouseY = 0.5;
      var scrollTarget = 0, scrollSmooth = 0;
      var running = false, alive = true;
      var idleTimer = null, snapTarget = null;
      var ANCHORS = [0, 0.5, 1];
      var motion = R.motionAllowed();

      function computeTarget() {
        var max = window.innerHeight * 1.4;
        scrollTarget = clamp(window.scrollY / max, 0, 1);
      }

      function render() {
        var p = scrollSmooth;
        var op = [0, 0, 0];

        if (count === 1) op = [1, 0, 0];
        else if (count === 2) {
          var t2 = easeInOutSine(p);
          op = [1 - t2, t2, 0];
        } else if (p < 0.5) {
          var ta = easeInOutSine(p / 0.5);
          op = [1 - ta, ta, 0];
        } else {
          var tb = easeInOutSine((p - 0.5) / 0.5);
          op = [0, 1 - tb, tb];
        }

        var mx = (mouseX - 0.5) * 2;
        var my = (mouseY - 0.5) * 2;
        var drift = [6, 11, 16];
        var sink = p * 18;

        for (var i = 0; i < 3; i++) {
          layers[i].style.opacity = op[i].toFixed(3);
          if (!motion) { layers[i].style.transform = ''; continue; }
          var d = drift[i];
          var scale = (1.02 + i * 0.012 + p * 0.03).toFixed(4);
          layers[i].style.transform =
            'translate3d(' + (-mx * d).toFixed(1) + 'px, ' +
            (-my * d - sink * (0.4 + i * 0.25)).toFixed(1) + 'px, 0) scale(' + scale + ')';
        }
      }

      function nearestAnchor(v) {
        var best = ANCHORS[0], bd = Infinity;
        ANCHORS.forEach(function (a) {
          var d = Math.abs(a - v);
          if (d < bd) { bd = d; best = a; }
        });
        return best;
      }

      function frame() {
        if (!alive) { running = false; return; }
        if (snapTarget === null) computeTarget();
        var goal = snapTarget !== null ? snapTarget : scrollTarget;
        var ease = snapTarget !== null ? 0.085 : 0.12;
        scrollSmooth += (goal - scrollSmooth) * ease;
        render();
        if (Math.abs(goal - scrollSmooth) > 0.0006) {
          requestAnimationFrame(frame);
        } else {
          scrollSmooth = goal;
          render();
          running = false;
          snapTarget = null;
        }
      }

      function kick() {
        if (!alive) return;
        snapTarget = null;
        if (!running) { running = true; requestAnimationFrame(frame); }
        clearTimeout(idleTimer);
        idleTimer = setTimeout(function () {
          computeTarget();
          if (scrollTarget > 0.02 && scrollTarget < 0.98) {
            snapTarget = nearestAnchor(scrollTarget);
            if (!running) { running = true; requestAnimationFrame(frame); }
          }
        }, 220);
      }

      var scrollQueued = false;
      function onScroll() {
        if (scrollQueued) return;      // coalesce bursts into one rAF tick
        scrollQueued = true;
        requestAnimationFrame(function () { scrollQueued = false; kick(); });
      }
      var finePointer = window.matchMedia('(pointer: fine)').matches;
      function onMouse(e) {
        if (!motion || !finePointer) return;
        mouseX = e.clientX / window.innerWidth;
        mouseY = e.clientY / window.innerHeight;
        kick();
      }
      function onResize() {
        computeTarget();
        scrollSmooth = scrollTarget;
        snapTarget = null;
        render();
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      if (finePointer) window.addEventListener('mousemove', onMouse, { passive: true });
      window.addEventListener('resize', onResize);
      onResize();

      return function () {
        alive = false;
        clearTimeout(idleTimer);
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('mousemove', onMouse);
        window.removeEventListener('resize', onResize);
      };
    }
  });

  /* ═══════════════════ PHILOSOPHY — SCROLL-DRIVEN PRESENCE CYCLE ═══════════════════
     Each .phil-reveal gets a --phs (presence 0–1) and --off (signed offset)
     CSS variable updated every frame from its distance to viewport centre.
     Also drives the 3D cross tilt, the thread fill and the chapter indicator. */

  R.register({
    name: 'PhilosophyCycle',
    scope: '[data-philosophy]',
    mount: function (root) {
      var alive = true;
      var motion = R.motionAllowed();

      var cache = [];
      function refresh() {
        cache = Array.prototype.slice.call(
          root.querySelectorAll('.phil-reveal, .phil-title-mega')
        );
      }
      refresh();

      function presence(el) {
        var r = el.getBoundingClientRect();
        var vh = window.innerHeight;
        var center = r.top + r.height / 2;
        var offset = center - vh / 2;
        var distance = Math.abs(offset);
        var innerZone = vh * 0.5;
        var maxDistance = vh * 1.0 + r.height / 2;
        var phs;
        if (distance < innerZone) phs = 1;
        else if (distance > maxDistance) phs = 0;
        else phs = 1 - smoothstep((distance - innerZone) / (maxDistance - innerZone));
        return { phs: phs, off: clamp(offset / maxDistance, -1, 1) };
      }

      // Hero letter dispersal offsets — random per letter, as in the source
      root.querySelectorAll('.phil-title-mega .phl-l').forEach(function (letter) {
        if (letter.dataset.rwDispersed === '1') return;
        letter.dataset.rwDispersed = '1';
        letter.style.setProperty('--dx', ((Math.random() - 0.5) * 360).toFixed(1));
        letter.style.setProperty('--dy', ((Math.random() - 0.5) * 240 - 50).toFixed(1));
        letter.style.setProperty('--rot', ((Math.random() - 0.5) * 140).toFixed(1));
      });

      var mouse = { tx: 0, ty: 0, x: 0, y: 0 };
      var finePointer = window.matchMedia('(pointer: fine)').matches;
      function onMouse(e) {
        if (!motion || !finePointer) return;
        mouse.tx = (e.clientX / window.innerWidth - 0.5) * 2;
        mouse.ty = (e.clientY / window.innerHeight - 0.5) * 2;
      }
      document.addEventListener('mousemove', onMouse, { passive: true });

      var indicator = root.querySelector('.phil-chapter-indicator');
      var indNum = indicator ? indicator.querySelector('.phil-ci-num') : null;
      var indLabel = indicator ? indicator.querySelector('.phil-ci-label') : null;
      var threadFill = root.querySelector('.phil-thread-fill');
      var threadDot = root.querySelector('.phil-thread-dot');
      var lastNum = '';

      function frame() {
        if (!alive) return;

        mouse.x += (mouse.tx - mouse.x) * 0.08;
        mouse.y += (mouse.ty - mouse.y) * 0.08;

        if (!cache.length) refresh();
        cache.forEach(function (el) {
          var p = presence(el);
          el.style.setProperty('--phs', p.phs.toFixed(3));
          el.style.setProperty('--off', p.off.toFixed(3));
        });

        var vh = window.innerHeight;

        if (motion) {
          root.querySelectorAll('.cross-3d').forEach(function (cross) {
            var r = cross.getBoundingClientRect();
            var sigY = clamp((r.top + r.height / 2 - vh / 2) / vh, -1, 1);
            cross.style.setProperty('--pmx', mouse.x.toFixed(3));
            cross.style.setProperty('--pmy', mouse.y.toFixed(3));
            cross.style.setProperty('--psr', (sigY * 8).toFixed(2));
          });
        }

        if (threadFill && threadDot) {
          var pageRect = root.getBoundingClientRect();
          var docTop = window.scrollY + pageRect.top;
          var docBottom = docTop + root.offsetHeight;
          var viewportCenter = window.scrollY + vh / 2;
          var progress = clamp((viewportCenter - docTop) / (docBottom - docTop), 0, 1);
          threadFill.style.height = (progress * 100).toFixed(2) + '%';
          threadDot.style.top = (progress * 100).toFixed(2) + '%';
        }

        if (indicator) {
          var bestCh = null, bestVisible = 0;
          root.querySelectorAll('.phil-chapter').forEach(function (ch) {
            var r = ch.getBoundingClientRect();
            var visible = Math.max(0, Math.min(vh, r.bottom) - Math.max(0, r.top));
            if (visible > bestVisible) { bestVisible = visible; bestCh = ch; }
          });
          if (bestCh) {
            var num = bestCh.getAttribute('data-phil-num') || '';
            var label = bestCh.getAttribute('data-phil-label') || '';
            if (num !== lastNum) {
              if (indNum) indNum.textContent = num;
              if (indLabel) indLabel.textContent = label;
              lastNum = num;
            }
            indicator.classList.add('is-shown');
          }
        }

        requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);

      function onResize() { refresh(); }
      window.addEventListener('resize', onResize);

      return function () {
        alive = false;
        document.removeEventListener('mousemove', onMouse);
        window.removeEventListener('resize', onResize);
      };
    }
  });

  /* ═══════════════════ WELLNESS RADAR ═══════════════════
     Six-vertex trace driven by the patient wellness score.

     The empty-data state is DESIGNED, not disabled: with no record the trace
     collapses to a small searching ring that breathes and slowly rotates, so
     the interface reads as monitoring-with-no-signal rather than broken.
     Radius is read from data-radar-max so the same module serves both the
     80px hero widget and the 160px wellness card.

     Pauses off-screen, respects prefers-reduced-motion, one rAF per instance. */

  R.register({
    name: 'WellnessRadar',
    scope: '[data-radar]',
    mount: function (root) {
      var trace = root.querySelector('[data-radar-trace]');
      if (!trace) return;

      var sweep = root.querySelector('.radar-sweep');
      var maxR = parseFloat(root.getAttribute('data-radar-max')) || 30;
      var motion = R.motionAllowed();
      var angles = [-90, -30, 30, 90, 150, 210];
      var alive = true;

      function enrolled() {
        var v = root.getAttribute('data-wellness');
        return v !== null && v !== '' && parseFloat(v) > 0;
      }

      function points(t) {
        var live = enrolled();
        var score = clamp(parseFloat(root.getAttribute('data-wellness') || '0') || 0, 0, 100);

        // Null dataset still draws a visible, moving ring.
        var base = live
          ? maxR * (score / 100)
          : maxR * 0.18;

        var amp = live ? 0.16 : 0.30;   // empty state breathes harder

        return angles.map(function (deg, i) {
          var rad = deg * Math.PI / 180;
          var flutter = motion ? Math.sin(t * (0.9 + i * 0.31) + i * 1.27) * amp : 0;
          var r = Math.max(4, base * (1 + flutter));
          return (r * Math.cos(rad)).toFixed(2) + ',' + (r * Math.sin(rad)).toFixed(2);
        }).join(' ');
      }

      function paintStatic() {
        trace.setAttribute('points', points(0));
        trace.setAttribute('data-state', enrolled() ? 'live' : 'null');
      }

      if (!motion) { paintStatic(); return; }

      var visible = true;
      var io = ('IntersectionObserver' in window)
        ? new IntersectionObserver(function (entries) {
            var was = visible;
            visible = entries[0].isIntersecting;
            if (visible && !was) requestAnimationFrame(tick);
          }, { rootMargin: '150px 0px' })
        : null;
      if (io) io.observe(root);

      function tick(now) {
        if (!alive) return;
        if (!visible) return;                 // IO resumes us
        var t = now / 1000;
        trace.setAttribute('points', points(t));
        if (sweep) sweep.setAttribute('transform', 'rotate(' + ((t * 22) % 360).toFixed(1) + ')');
        requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);

      function onPatient() {
        trace.setAttribute('data-state', enrolled() ? 'live' : 'null');
      }
      document.addEventListener('rowman:patient:render', onPatient);
      onPatient();

      return function () {
        alive = false;
        if (io) io.disconnect();
        document.removeEventListener('rowman:patient:render', onPatient);
      };
    }
  });

  /* ═══════════════════ GIFT CARD — 3D FLIP + CURSOR TILT ═══════════════════ */

  R.register({
    name: 'GiftCard3D',
    scope: '[data-giftcard-scene]',
    mount: function (scene) {
      var card = scene.querySelector('[data-giftcard]');
      if (!card) return;

      if (!R.motionAllowed()) {
        card.style.setProperty('--rot', '0deg');
        card.style.setProperty('--tilt', '0deg');
        card.style.setProperty('--float', '0px');
        return;
      }

      var alive = true;
      var flipStart = null;
      var tx = 0, ty = 0, cx = 0, cy = 0;
      var visible = true;
      var gio = ('IntersectionObserver' in window)
        ? new IntersectionObserver(function (entries) {
            var was = visible;
            visible = entries[0].isIntersecting;
            if (visible && !was) requestAnimationFrame(frame);
          }, { rootMargin: '120px 0px' })
        : null;
      if (gio) gio.observe(scene);

      function onMove(e) {
        var rect = scene.getBoundingClientRect();
        tx = ((e.clientX - rect.left) / rect.width - 0.5) * 26;
        ty = -((e.clientY - rect.top) / rect.height - 0.5) * 16;
      }
      function onLeave() { tx = 0; ty = 0; }

      function frame() {
        if (!alive) return;
        if (!visible) return;
        var now = performance.now();
        if (flipStart === null) flipStart = now;

        var progress = clamp((now - flipStart) / 1000 / 0.9, 0, 1);
        var eased = 1 - Math.pow(1 - progress, 3);
        var flipRot = 180 * (1 - eased);

        var t = now / 1000;
        var breatheTilt = Math.sin(t * 0.7) * 4;
        var breatheFloat = Math.sin(t * 0.6) * 8;

        cx += (tx - cx) * 0.09;
        cy += (ty - cy) * 0.09;

        card.style.setProperty('--rot', (flipRot + cx).toFixed(2) + 'deg');
        card.style.setProperty('--tilt', (breatheTilt + cy).toFixed(2) + 'deg');
        card.style.setProperty('--float', breatheFloat.toFixed(2) + 'px');

        requestAnimationFrame(frame);
      }

      scene.addEventListener('mousemove', onMove);
      scene.addEventListener('mouseleave', onLeave);
      requestAnimationFrame(frame);

      return function () {
        alive = false;
        if (gio) gio.disconnect();
        scene.removeEventListener('mousemove', onMove);
        scene.removeEventListener('mouseleave', onLeave);
      };
    }
  });

  /* ═══════════════════ GIFT CARD — DENOMINATION SELECTOR ═══════════════════
     Denominations are Shopify gift-card product variants. Selecting one
     updates the card face and the add-to-cart variant id. */

  R.register({
    name: 'GiftCardAmount',
    scope: '[data-giftcard-amounts]',
    mount: function (root) {
      function apply(btn) {
        if (!btn || btn.hasAttribute('disabled')) return;

        root.querySelectorAll('[data-gc-amount]').forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-checked', 'true');

        var label = btn.getAttribute('data-gc-amount');

        // Card face and CTA both show the selected denomination
        document.querySelectorAll('[data-giftcard-value]').forEach(function (el) {
          el.textContent = label;
        });

        // The real Shopify variant id is what gets posted to the cart
        var input = document.querySelector('[data-gc-variant-input]');
        if (input) input.value = btn.getAttribute('data-variant-id') || '';
      }

      function onClick(e) {
        var btn = e.target.closest('[data-gc-amount]');
        if (!btn || !root.contains(btn)) return;
        e.preventDefault();
        apply(btn);
      }

      function onKey(e) {
        if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].indexOf(e.key) === -1) return;
        var items = Array.prototype.filter.call(
          root.querySelectorAll('[data-gc-amount]'),
          function (b) { return !b.hasAttribute('disabled'); }
        );
        var i = items.indexOf(document.activeElement);
        if (i === -1) return;
        e.preventDefault();
        var next = (e.key === 'ArrowRight' || e.key === 'ArrowDown')
          ? items[(i + 1) % items.length]
          : items[(i - 1 + items.length) % items.length];
        next.focus();
        apply(next);
      }

      root.addEventListener('click', onClick);
      root.addEventListener('keydown', onKey);

      // Select the first available denomination on load
      apply(root.querySelector('[data-gc-amount].is-active:not([disabled])') ||
            root.querySelector('[data-gc-amount]:not([disabled])'));

      return function () {
        root.removeEventListener('click', onClick);
        root.removeEventListener('keydown', onKey);
      };
    }
  });

  /* ═══════════════════ IMPACT TRACKER COUNT-UP ═══════════════════
     Counts to the value supplied in the section setting. Static when motion
     is reduced so the final figure is always readable. */

  R.register({
    name: 'ImpactCounter',
    scope: '[data-impact-counter]',
    mount: function (el) {
      var target = parseFloat(el.getAttribute('data-impact-target') || '0');
      var prefix = el.getAttribute('data-impact-prefix') || '';
      var suffix = el.getAttribute('data-impact-suffix') || '';

      function fmt(v) {
        return prefix + Math.round(v).toLocaleString() + suffix;
      }

      if (!R.motionAllowed() || !target) {
        el.textContent = fmt(target);
        return;
      }

      var started = false;
      var io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting || started) return;
        started = true;
        io.disconnect();
        var t0 = performance.now();
        (function step(now) {
          var p = clamp((now - t0) / 1600, 0, 1);
          el.textContent = fmt(target * (1 - Math.pow(1 - p, 3)));
          if (p < 1) requestAnimationFrame(step);
        })(t0);
      }, { threshold: 0.4 });
      io.observe(el);

      return function () { io.disconnect(); };
    }
  });

}());
